// Shared "Out of Stars" sheet (remix-from-link contract / AI-Star billing UX).
// Shown when an AI action fails with WALLET_INSUFFICIENT: a warm, non-blocking
// beat that says the work is safe and hands over the honest next steps.
//
// Two audiences (parent studio parity — `parentActor` is threaded from the
// host's existing principal knowledge, never derived here):
//   - KID (default): "Ask a parent to top up" sends a real `top_up_request` to the
//     parent's inbox (this sheet performs NO wallet action); if it cannot be sent
//     it shows the honest how-to instead of pretending;
//   - PARENT: they ARE the wallet owner — the primary action links straight to
//     the Portal top-up page.
//   - INDEPENDENT STUDENT (D-SSU-23): they own their wallet too, so the primary
//     action is their own Learn top-up page, never "ask a parent". Read from the
//     cached kid `/auth/me` (the Learn shell has already loaded it), so no host
//     has to thread one more prop.

import { useState } from 'react';
import { Mail, Star, Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';

import { STUDENT_SELF_TOPUP_PATH, useCachedStudentManager } from '@/auth/familyManager';
import { askParentToTopUp } from '@/pages/learn/courses/selfPacedApi';

type AskState = 'idle' | 'sending' | 'sent' | 'failed';

interface OutOfStarsSheetProps {
  /** "Keep building without AI" — dismiss and carry on (nothing was lost). */
  onDismiss: () => void;
  /** The acting principal is the PARENT → the primary action is the Portal
   *  top-up link, not "ask a parent". */
  parentActor?: boolean;
  /** Surface-specific explanation; defaults to the Creative Code Studio copy. */
  body?: string;
  /** Surface-specific dismiss label; defaults to "Keep building without AI". */
  dismissLabel?: string;
}

const DEFAULT_BODY =
  'AI needs fuel to make a new change, and your Star tank is empty. Your game is safe — you can still play it and edit the code yourself.';
const DEFAULT_DISMISS = 'Keep building without AI';

export function OutOfStarsSheet({
  onDismiss,
  parentActor = false,
  body = DEFAULT_BODY,
  dismissLabel = DEFAULT_DISMISS,
}: OutOfStarsSheetProps) {
  const [ask, setAsk] = useState<AskState>('idle');
  const studentManager = useCachedStudentManager() && !parentActor;
  const askParent = !parentActor && !studentManager;

  // Sends a real `top_up_request` to the parent's inbox. If it cannot be sent (a workshop walk-in
  // has no parent, or the network fails) the sheet falls back to the how-to text.
  const sendAsk = () => {
    setAsk('sending');
    askParentToTopUp(0, 'out_of_stars').then(
      () => setAsk('sent'),
      () => setAsk('failed'),
    );
  };
  return (
    <div
      className="fixed inset-0 z-[1100] flex items-end justify-center bg-black/60 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Out of Stars"
      data-testid="out-of-stars-sheet"
    >
      <div className="w-full max-w-sm rounded-3xl bg-canvas-pure p-6 text-center shadow-card-soft">
        <Star size={36} aria-hidden className="mx-auto text-brand-sunshine" />
        <h2 className="mt-2 text-[20px] font-bold text-ink">Out of Stars!</h2>
        <p className="lead-text mt-2 text-[14px]">{body}</p>
        {askParent && ask === 'sent' && (
          <p
            data-testid="out-of-stars-asked"
            role="status"
            className="mt-4 rounded-2xl bg-wash-sunshine px-4 py-3 text-[13px] font-semibold text-ink"
          >
            Asked! Your parent will see your request in the Family Portal.
          </p>
        )}
        {askParent && ask === 'failed' && (
          <p
            data-testid="out-of-stars-how"
            className="mt-4 rounded-2xl bg-wash-sunshine px-4 py-3 text-[13px] font-semibold text-ink"
          >
            We couldn&apos;t send that. Grown-ups add Stars on the Family Portal&apos;s Wallet page
            — show them this screen!
          </p>
        )}
        {parentActor ? (
          <Link
            to="/portal/wallet/topup"
            data-testid="out-of-stars-topup"
            className="btn-pill-primary mt-5 inline-flex w-full items-center justify-center gap-1.5"
          >
            <Wallet size={16} aria-hidden /> Top up in the Portal
          </Link>
        ) : studentManager ? (
          <Link
            to={STUDENT_SELF_TOPUP_PATH}
            data-testid="out-of-stars-self-topup"
            className="btn-pill-primary mt-5 inline-flex w-full items-center justify-center gap-1.5"
          >
            <Wallet size={16} aria-hidden /> Top up my Stars
          </Link>
        ) : (
          <button
            type="button"
            data-testid="out-of-stars-ask"
            onClick={sendAsk}
            disabled={ask === 'sending' || ask === 'sent'}
            className="btn-pill-primary mt-5 inline-flex w-full items-center justify-center gap-1.5 disabled:opacity-60"
          >
            <Mail size={16} aria-hidden />{' '}
            {ask === 'sending' ? 'Asking…' : ask === 'sent' ? 'Asked' : 'Ask a parent to top up'}
          </button>
        )}
        <button
          type="button"
          data-testid="out-of-stars-dismiss"
          onClick={onDismiss}
          className="btn-pill-ghost mt-2 w-full"
        >
          {dismissLabel}
        </button>
      </div>
    </div>
  );
}
